import type { ReactNode } from "react";
import { Link } from "react-router";
import { PLAN_LABEL, PLAN_LIMITS, PLAN_PERKS, SELLER_PLANS, type SellerPlan } from "../../shared/market";
import type { Showcase, ShowcaseKind } from "../../shared/marketStats";
import { formatArs } from "../../shared/money";
import { useI18n } from "../i18n/I18nProvider";
import { track } from "../lib/analytics";
import { useAsync } from "../lib/useAsync";
import { marketApi } from "./api";
import { Dialog } from "./components";

/** The cheapest plan with each perk. */
const PLAN_FOR: Record<ShowcaseKind, SellerPlan> = {
  demand: SELLER_PLANS.find((p) => PLAN_PERKS[p].demandAlerts)!,
  customers: SELLER_PLANS.find((p) => PLAN_PERKS[p].customers)!,
  emails: SELLER_PLANS.find((p) => PLAN_PERKS[p].shopEmailsPerMonth > 0)!,
  price: SELLER_PLANS.find((p) => PLAN_PERKS[p].insights)!,
  feature: SELLER_PLANS.find((p) => PLAN_PERKS[p].featuredPerMonth > 0)!,
  first_sale: SELLER_PLANS.find((p) => PLAN_PERKS[p].customers)!,
};

/**
 * Where a way up to a paid plan was taken (GA4's `source` on "upgrade_cta_click", "plan_request" and
 * "plan_subscribe_start"): the plan line on Inicio, a warning, a padlocked quick action, a limit hit
 * (cupo, precio), a showcase ("vidriera:<kind>") or the plan page itself ("mi-plan").
 */
export type UpgradeSource = "linea-plan" | "aviso" | `accion:${"cupon" | "reserva"}` | "cupo" | "precio" | "mi-plan" | `vidriera:${string}`;
const SOURCE_KEY = "pf-upgrade-source";

/**
 * A way up was taken: counted, and remembered for this tab so the plan request says where it came
 * from. The plan page's own "Ver planes" ("mi-plan") is the second step of a way up taken before
 * (the plan line, a warning…): it doesn't replace that one.
 */
export function noteUpgrade(source: UpgradeSource): void {
  track("upgrade_cta_click", { source });
  try {
    if (source === "mi-plan" && sessionStorage.getItem(SOURCE_KEY)) return;
    sessionStorage.setItem(SOURCE_KEY, source);
  } catch {
    // Storage blocked (private mode): the click still counts.
  }
}

/** Where the plan request or subscription now being sent started (once: then forgotten); `fallback` is the page's ?desde=. */
export function takeUpgradeSource(fallback?: string | null): string {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(SOURCE_KEY);
    sessionStorage.removeItem(SOURCE_KEY);
  } catch {
    // Storage blocked: only ?desde= or "directo".
  }
  return stored ?? fallback ?? "directo";
}

/** Where every "get the plan" button goes, and which one it was (GA4: what sells plans). */
export function UpgradeLink({ source, className = "btn btn-primary btn-sm", children }: { source: UpgradeSource; className?: string; children: ReactNode }) {
  return (
    <Link to={`/vender/planes?desde=${encodeURIComponent(source)}`} className={className} onClick={() => noteUpgrade(source)}>
      {children}
    </Link>
  );
}

/**
 * A paid perk with the shop's own number: the title, the number, rows that stay blurred (placeholders:
 * the real ones never reach the page) and a button that says what you'd do, not "Mejorar plan".
 */
export function ShowcaseCard({ showcase }: { showcase: Showcase }) {
  const { t } = useI18n();
  const { kind, count } = showcase;
  const plan = PLAN_FOR[kind];
  return (
    <section className="plan-card showcase" aria-labelledby={`showcase-${kind}`}>
      <p className="showcase-head">
        <strong id={`showcase-${kind}`}>{t(`show.${kind}.title` as never)}</strong>
        <span className="pill">{PLAN_LABEL[plan]}</span>
      </p>
      <p className="showcase-number">{t(`show.${kind}.number` as never, { count })}</p>
      <ul className="showcase-rows" aria-hidden="true">
        <li>Charizard ex · 3 · $ 48.500</li>
        <li>Pikachu VMAX · 2 · $ 12.000</li>
        <li>Umbreon V · 1 · $ 31.000</li>
      </ul>
      <p className="muted small">{t(`show.${kind}.hint` as never, { plan: PLAN_LABEL[plan], emails: PLAN_PERKS[plan].shopEmailsPerMonth })}</p>
      <UpgradeLink source={`vidriera:${kind}`}>{t(`show.${kind}.verb` as never)}</UpgradeLink>
    </section>
  );
}

/** A coupon code for the shop, as an example: its handle in capitals ("CARTASANA10"), or TUTIENDA10. */
export function sampleCouponCode(handle: string): string {
  const code = `${handle.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 14)}10`;
  return /^[A-Z0-9]{4,16}$/.test(code) ? code : "TUTIENDA10";
}

/** Cupones, for a plan without them: what one would look like with the shop's name, and the way to it. */
export function CouponShowcase({ handle }: { handle: string }) {
  const { t } = useI18n();
  const plan = SELLER_PLANS.find((p) => PLAN_PERKS[p].coupons > 0)!;
  return (
    <section className="plan-card showcase" aria-labelledby="showcase-coupons">
      <p className="showcase-head">
        <strong id="showcase-coupons">{t("show.coupons.title")}</strong>
        <span className="pill">{PLAN_LABEL[plan]}</span>
      </p>
      <p className="showcase-number">{t("show.coupons.number")}</p>
      <p className="coupon-sample">
        <code>{sampleCouponCode(handle)}</code> <span className="muted small">{t("show.coupons.sample")}</span>
      </p>
      <p className="muted small">{t("show.coupons.hint", { plan: PLAN_LABEL[plan] })}</p>
      <UpgradeLink source="vidriera:cupones">{t("show.coupons.verb")}</UpgradeLink>
    </section>
  );
}

/** Tienda personalizada, for a plan without it: the shop's own page with a sample banner, nothing saved. */
export function ShopShowcase({ name, tagline, links }: { name: string; tagline: string; links: number }) {
  const { t } = useI18n();
  const plan = SELLER_PLANS.find((p) => PLAN_PERKS[p].customShop)!;
  return (
    <section className="plan-card showcase" aria-labelledby="showcase-shop">
      <p className="showcase-head">
        <strong id="showcase-shop">{t("mkt.shop.title")}</strong>
        <span className="pill">{PLAN_LABEL[plan]}</span>
      </p>
      <div className="shop-sample" aria-hidden="true">
        <div className="shop-sample-banner" />
        <div className="shop-sample-body">
          <span className="shop-sample-logo">{name.charAt(0).toUpperCase()}</span>
          <span>
            <strong>{name}</strong>
            <span className="muted small">{tagline || t("show.shop.tagline")}</span>
          </span>
        </div>
      </div>
      <p className="showcase-number">{t("show.shop.number")}</p>
      <p className="muted small">{t("mkt.shop.teaser", { plan: PLAN_LABEL[plan], pins: PLAN_PERKS[plan].shopPins })}{links > 0 ? ` ${t("show.shop.links", { count: links })}` : ""}</p>
      <UpgradeLink source="vidriera:tienda">{t("show.shop.verb")}</UpgradeLink>
    </section>
  );
}

/** In a section the plan doesn't include: its showcase when the shop's number is above zero, else what it was (`children`). */
export function ShowcaseOr({ kind, children }: { kind: ShowcaseKind; children: ReactNode }) {
  const data = useAsync((signal) => marketApi.showcase(signal), []);
  if (!data.data && !data.error) return null;
  const count = data.data?.counts?.[kind] ?? 0;
  return count > 0 ? <ShowcaseCard showcase={{ kind, count }} /> : <>{children}</>;
}

/**
 * The moment of highest intent: a card that doesn't fit the plan (its live listings, or its price).
 * The listing is saved, paused: nothing is lost. One way up, one way around.
 */
export function LimitDialog({
  kind,
  plan,
  live,
  price = 0,
  onClose,
  onPauseOther,
}: {
  kind: "listing" | "price";
  plan: SellerPlan;
  live: number;
  /** The price that didn't fit ("price"): the plan offered is one that takes it. */
  price?: number;
  onClose: () => void;
  /** "Pausar otra": to the list of live ones. */
  onPauseOther: () => void;
}) {
  const { t, locale } = useI18n();
  const rank = SELLER_PLANS.indexOf(plan);
  const next = SELLER_PLANS.find((p, i) => i > rank && (kind === "listing" ? (PLAN_LIMITS[p].listings ?? Infinity) > live : (PLAN_LIMITS[p].maxPriceArs ?? Infinity) >= price)) ?? "vmax";
  const max = kind === "listing" ? PLAN_LIMITS[plan].listings ?? 0 : PLAN_LIMITS[plan].maxPriceArs ?? 0;
  const nextMax = kind === "listing" ? PLAN_LIMITS[next].listings : PLAN_LIMITS[next].maxPriceArs;
  return (
    <Dialog title={t(kind === "listing" ? "limit.listingTitle" : "limit.priceTitle")} onClose={onClose}>
      <p>
        {kind === "listing"
          ? t("limit.listingText", { live, plan: PLAN_LABEL[plan] })
          : t("limit.priceText", { plan: PLAN_LABEL[plan], max: formatArs(max, locale) })}
      </p>
      <p>
        {kind === "listing"
          ? nextMax === null
            ? t("limit.listingNextNoLimit", { plan: PLAN_LABEL[next] })
            : t("limit.listingNext", { plan: PLAN_LABEL[next], more: nextMax - live - 1 })
          : nextMax === null
            ? t("limit.priceNextNoLimit", { plan: PLAN_LABEL[next] })
            : t("limit.priceNext", { plan: PLAN_LABEL[next], max: formatArs(nextMax, locale) })}{" "}
        {PLAN_PERKS[next].featuredPerMonth > 0 && t("limit.featured", { count: PLAN_PERKS[next].featuredPerMonth })}
      </p>
      <p className="muted small">{t("limit.saved")}</p>
      <div className="btn-row">
        <UpgradeLink source={kind === "listing" ? "cupo" : "precio"} className="btn btn-primary">
          {t("limit.upgrade", { plan: PLAN_LABEL[next] })}
        </UpgradeLink>
        <button type="button" className="btn btn-outline" onClick={kind === "listing" ? onPauseOther : onClose}>
          {kind === "listing" ? t("limit.pauseOther") : t("limit.lowerPrice")}
        </button>
      </div>
    </Dialog>
  );
}
